import React, { useState, useEffect } from "react";
import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    Modal,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    capitalize,
    Chip,
} from "@mui/material";
import * as Yup from "yup";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import queryGraphql from "../../../helpers/ApiInterface";
import { useAuth } from "../../../helpers/AuthProvider";
import { Close } from "@mui/icons-material";
import { Formik, ErrorMessage, Form } from "formik";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import MiniBot from "../../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";

// --- Interfaces ---
interface CostLabelPair {
    cost: string;
    label: string;
}

interface ConsumerGroupData {
    id: string;
    name: string;
    status: string;
    description?: string;
    rate?: string;
    burst?: string;
    quotaRequests?: string;
    quotaPeriod?: string;
    reasonForRejection?: string;
    costLabelPairs: CostLabelPair[];
    consumerGroupOwner: { id: string; name: string; };
    consumerGroupApprover?: { id: string; name: string; };
}

const ValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
});

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null; }> = ({ label, value }) => {
    const displayValue = value ?? '-';
    
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>
                {typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}
            </Typography>
        </Box>
    );
};

const ConsumerGroupApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [consumerGroup, setConsumerGroup] = useState<ConsumerGroupData | null>(null);
    const [openModal, setOpenModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [repoUrl, setRepoUrl] =  useState<string | null>(null);
    const [jiraIssueKey, setJiraIssueKey] = useState<string | null>(null);

    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            try {
                const data = await queryGraphql(`
                    query GetConsumerGroupDetailsForApproval($id: String!) {
                        getConsumerGroup(id: $id) {
                            id
                            name
                            status
                            description
                            rate
                            burst
                            quotaRequests
                            quotaPeriod
                            reasonForRejection
                            costLabelPairs { cost, label }
                            consumerGroupOwner { id, name }
                            consumerGroupApprover { id, name }
                        }
                        resourceStatus(id: $id) {
                            jiraIssueKey
                            projectUrl
                        }
                    }`, { id: id });

                if (data.getConsumerGroup) {
                    setConsumerGroup(data.getConsumerGroup);
                }
                if (data.resourceStatus) {
                    setJiraIssueKey(data.resourceStatus.jiraIssueKey);
                    setRepoUrl(data.resourceStatus.projectUrl);
                }
            } catch (error) { 
                console.error("Error fetching Consumer Group details:", error); 
            }
        };
        fetchData();
    }, [id]);
    
    const handleApprove = async () => {
        if (!id) return;
        try {
            const approvalData = await queryGraphql(
                `mutation ApproveConsumerGroup($input: ApproveConsumerGroupInput!) { approveConsumerGroup(input: $input) { id } }`,
                { input: { id } }
            );
            if (approvalData.approveConsumerGroup?.id) {
                setOpenApproveModal(false);
                navigate("/consumer-group/requests");
            }
        } catch (error) { console.error("Error during approval:", error); }
    };
    
    const handleReject = async (reasonForRejection: string) => {
        if (!id) return;
        try {
            const rejectionData = await queryGraphql(
                `mutation RejectConsumerGroup($input: RejectConsumerGroupInput!) { rejectConsumerGroup(input: $input) { id } }`,
                { input: { id: id, reasonForRejection } }
            );
            if (rejectionData.rejectConsumerGroup?.id) {
                setOpenModal(false);
                navigate("/consumer-group/requests");
            }
        } catch (error) { console.error("Error during rejection:", error); }
    };

    const statusDisplay = consumerGroup?.status ? capitalize(consumerGroup.status) : "Status Unknown";
    const statusBgColor = consumerGroup?.status === "active" || consumerGroup?.status === "approved" ? "#12B76A10" : consumerGroup?.status === "rejected" ? "#F0443810" : "#FDB02210";
    const statusIcon = consumerGroup?.status === "active" || consumerGroup?.status === "approved" ? "/assets/tickcircle.svg" : consumerGroup?.status === "rejected" ? "/assets/reject.svg" : "/assets/clock.svg";
    
    const jiraLink = jiraIssueKey ? `https://searce-intellicore.atlassian.net/browse/${jiraIssueKey}` : undefined;

    if (!consumerGroup) return <Container sx={{p: 4, textAlign: 'center'}}>Loading details...</Container>;

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/consumer-group/requests" onClick={(e) => { e.preventDefault(); navigate("/consumer-group/requests") }}> Consumer Group Requests </Link>
                    <Typography color="text.primary">Consumer Group Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/consumer-group/requests")}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">Consumer Group Details</Typography>
                </Box>
            </Box>
            <Grid container justifyContent="center">
                <Grid item xs={11}> 
                    <Box my={2}>
                        <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                    <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{consumerGroup.name}</Typography>
                                    <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${consumerGroup.consumerGroupOwner?.name}`}</Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{textTransform: 'capitalize'}}>{statusDisplay}</Typography>
                                </Box>
                            </Box>
                        </Box>
                        
                        <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                            <Typography variant="subtitle1" fontFamily="Gilroy-Medium" sx={{ mb: 2 }}>Throttling & Quota Settings</Typography>
                            <Grid container spacing={2.5}>
                                <Grid item xs={6} md={3}><DetailItem label="Rate Limit" value={consumerGroup.rate ? `${consumerGroup.rate} req/s` : null} /></Grid>
                                <Grid item xs={6} md={3}><DetailItem label="Burst Limit" value={consumerGroup.burst ? `${consumerGroup.burst} req/s` : null} /></Grid>
                                <Grid item xs={6} md={3}><DetailItem label="Quota" value={consumerGroup.quotaRequests ? `${consumerGroup.quotaRequests} / ${capitalize(consumerGroup.quotaPeriod || '')}` : null} /></Grid>
                                {repoUrl && <Grid item xs={6} md={3}>
                                     <Typography variant="caption" display="block" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}>Configuration Repo</Typography>
                                     <Tooltip title={<Link href={repoUrl} target="_blank" rel="noopener noreferrer" sx={{ color: '#ffffff' }}>Repository URL</Link>} slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}>
                                         <IconButton sx={{p: 0}}><Avatar src="/assets/info.svg" alt="info" sx={{ height: 16, width: 16, cursor: 'pointer' }}/></IconButton>
                                     </Tooltip>
                                </Grid>}
                                {jiraLink && jiraIssueKey && (
                                    <Grid item xs={6} md={3}>
                                        <Box mb={2}>
                                            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">Jira Ticket</Typography>
                                            <Link href={jiraLink} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '14px', fontFamily: 'Gilroy-Semibold', wordBreak: 'break-all' }}>{jiraIssueKey}</Link>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>

                            <Divider sx={{ my: 2.5 }} />

                            <Typography variant="subtitle1" fontFamily="Gilroy-Medium" sx={{ mb: 2 }}>Description</Typography>
                            <DetailItem label="" value={consumerGroup.description} />

                            <Divider sx={{ my: 2.5 }} />

                            <Typography variant="subtitle1" fontFamily="Gilroy-Medium" sx={{ mb: 2 }}>Tags / Labels</Typography>
                            <Box>
                                {consumerGroup.costLabelPairs.length > 0 ? (
                                    consumerGroup.costLabelPairs.map(pair => (
                                        <Chip key={pair.cost} label={`${pair.cost}: ${pair.label}`} sx={{ mr: 1, mb: 1, fontFamily: 'Gilroy-Regular' }} />
                                    ))
                                ) : (
                                     <Typography fontFamily="Gilroy-Regular" fontSize="14px" color="text.secondary">No tags provided.</Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    
                    {consumerGroup?.status === "pending" && (
                        <Box display="flex" justifyContent="flex-end" gap={2} my={3}>
                            <Button sx={{ color: "black",borderColor: "black",fontWeight: "bold",textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px" }} variant="outlined" size="large" disabled={isViewer} onClick={() => setOpenModal(true)}>Reject Request</Button>
                            <Button sx={{ backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px" }} variant="contained" size="large" disabled={isViewer} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                        </Box>
                    )}

                    <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 500 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Request</Typography><IconButton onClick={() => setOpenApproveModal(false)}><Close fontSize="small" /></IconButton></Box>
                            <Divider />
                            <Box p={3}><Typography>Are you sure you want to approve this Consumer Group request?</Typography></Box>
                            <Divider />
                            <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                <Button variant="outlined" onClick={() => setOpenApproveModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                <Button onClick={handleApprove} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Approve</Button>
                            </Box>
                        </Box>
                    </Modal>

                    <Modal open={openModal} onClose={() => setOpenModal(false)}>
                        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                            <Formik
                                initialValues={{ reasonForRejection: "" }}
                                validationSchema={ValidationSchema}
                                onSubmit={({ reasonForRejection }) => handleReject(reasonForRejection)}
                            >
                                {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                                    <Form id="reject-cg-form">
                                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Consumer Group Request</Typography><IconButton onClick={() => setOpenModal(false)}><Close fontSize="small" /></IconButton></Box>
                                        <Divider />
                                        <Box sx={{ p: 3 }}>
                                            <CompulsoryFieldsLabel fieldName="Comments" />
                                            <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                            <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
                                        </Box>
                                        <Divider />
                                        <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                            <Button variant="outlined" onClick={() => setOpenModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                            <Button type="submit" form="reject-cg-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' } }} variant="contained">Reject</Button>
                                        </Box>
                                    </Form>
                                )}
                            </Formik>
                        </Box>
                    </Modal>
                </Grid>
            </Grid>
        </Container>
    );
};

const ConsumerGroupApproveFormPage: React.FC = () => {
    const theme = useTheme();
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
    const handleDrawerStateChange = (isOpen: boolean) => {
        setIsDrawerOpen(isOpen);
        if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpen));
    };
    const currentMarginLeft = isDrawerOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-consumer-group"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
                <ConsumerGroupApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default ConsumerGroupApproveFormPage;